import {
	createContext,
	useCallback,
	type ReactNode,
	useContext,
	useEffect,
	useLayoutEffect,
	useRef,
	useState
} from "react"
import { toast } from "sonner"
import { authClient } from "~/lib/auth-client"
import {
	activeThemeUserKey,
	applyTheme,
	cacheThemePreference,
	defaultThemePreference,
	parseThemePreference,
	readActiveThemeUser,
	readThemePreference,
	relativeLuminance,
	themeStoragePrefix
} from "~/lib/theme"
import { api } from "~/trpc/react"

const ThemeContext = createContext({
	preference: defaultThemePreference,
	colorScheme: "dark" as "dark" | "light",
	ready: false,
	dirty: false,
	saving: false,
	resetPreference: () => {},
	savePreference: async () => {},
	updatePreference: (
		_update: Partial<typeof defaultThemePreference>
	): boolean => false
})

export function ThemeProvider({ children }: { children: ReactNode }) {
	const { data: session, isPending } = authClient.useSession()
	const userId = session?.user.id ?? null
	const currentUserId = useRef(userId)
	const { client } = api.useUtils()
	const [retry, setRetry] = useState(0)
	const [state, setState] = useState(() => {
		const preference = readThemePreference(readActiveThemeUser())
		return { preference, saved: preference, ready: false, saving: false }
	})
	const dirty = JSON.stringify(state.preference) !== JSON.stringify(state.saved)

	useLayoutEffect(() => {
		currentUserId.current = userId
		if (isPending) return
		const preference = readThemePreference(userId)
		applyTheme(preference)
		setState({ preference, saved: preference, ready: false, saving: false })
		try {
			if (userId) localStorage.setItem(activeThemeUserKey, userId)
			else localStorage.removeItem(activeThemeUserKey)
		} catch {
			/* Browser caching is optional. */
		}
	}, [isPending, userId])

	useEffect(() => {
		if (isPending || !userId) return
		let active = true
		void (async () => {
			try {
				const result = await client.user.getThemePreference.query()
				if (!active || result.userId !== userId) return
				const preference =
					result.preference ??
					(await client.user.setThemePreference.mutate({
						userId,
						preference: readThemePreference(userId),
						initializeOnly: true
					}))
				if (!active) return
				toast.dismiss("theme-load")
				cacheThemePreference(userId, preference)
				applyTheme(preference)
				setState({ preference, saved: preference, ready: true, saving: false })
			} catch {
				if (active)
					toast.error("Could not load your account theme.", {
						id: "theme-load",
						duration: Number.POSITIVE_INFINITY,
						action: {
							label: "Retry",
							onClick: () => setRetry((value) => value + 1)
						}
					})
			}
		})()
		return () => {
			active = false
			toast.dismiss("theme-load")
		}
	}, [client, isPending, userId, retry])

	useEffect(() => {
		const onStorage = (event: StorageEvent) => {
			if (!userId || state.saving || !state.ready) return
			if (event.key !== `${themeStoragePrefix}${userId}`) return
			const preference = readThemePreference(userId)
			if (dirty) {
				setState((previous) => ({ ...previous, saved: preference }))
				return
			}
			applyTheme(preference)
			setState({ preference, saved: preference, ready: true, saving: false })
		}
		window.addEventListener("storage", onStorage)
		return () => window.removeEventListener("storage", onStorage)
	}, [userId, dirty, state.saving, state.ready])

	const savedPreference = useRef(state.saved)
	useLayoutEffect(() => {
		savedPreference.current = state.saved
	}, [state.saved])
	const resetPreference = useCallback(() => {
		applyTheme(savedPreference.current)
		setState((previous) => ({ ...previous, preference: previous.saved }))
	}, [])

	return (
		<ThemeContext.Provider
			value={{
				preference: state.preference,
				dirty,
				saving: state.saving,
				resetPreference,
				savePreference: async () => {
					if (!userId || !state.ready || state.saving) return
					setState((previous) => ({ ...previous, saving: true }))
					try {
						const preference = await client.user.setThemePreference.mutate({
							userId,
							preference: state.preference
						})
						if (currentUserId.current !== userId) return
						cacheThemePreference(userId, preference)
						applyTheme(preference)
						setState({
							preference,
							saved: preference,
							ready: true,
							saving: false
						})
					} finally {
						if (currentUserId.current === userId)
							setState((previous) => ({ ...previous, saving: false }))
					}
				},
				colorScheme:
					state.preference.theme === "light" ||
					(state.preference.theme === "custom" &&
						relativeLuminance(state.preference.background) >= 0.179)
						? "light"
						: "dark",
				ready: !isPending && Boolean(userId) && state.ready && !state.saving,
				updatePreference: (update) => {
					if (!userId || isPending || !state.ready || state.saving) return false
					const preference = parseThemePreference({
						...state.preference,
						...update
					})
					applyTheme(preference)
					setState((previous) => ({ ...previous, preference }))
					return true
				}
			}}
		>
			{children}
		</ThemeContext.Provider>
	)
}

export const useTheme = () => useContext(ThemeContext)
